import { HttpErrorResponse } from '@angular/common/http';
import { inject, Service } from '@angular/core';

import { ApiError } from '@core/http/api-error';
import { TranslationFacade } from '@core/i18n/application/translation.facade';
import { AlertService } from '@shared/feedback/alert/alert.service';
import {
  OrgError,
  ORG_REASON_KEYS,
  OrgReason,
  orgMessageIsTrustworthy,
  orgMessageKeyFor,
  toOrgReason,
} from '../domain/org.errors';

/**
 * OrgErrorMapper — THE one place a B2B failure becomes something a member reads.
 *
 * Every `/v3/org/*` call is issued `{ silent: true }` (see
 * `OrganizationsApiClient`), so `AppHttp`'s automatic toast never fires for B2B
 * and this mapper owns the whole decision. It exists because §1.3 makes the
 * naive path actively wrong:
 *
 *   `customResponse()` OVERWRITES `message` on every 403 with a generic access
 *   sentence, whatever the controller passed. The real reason survives only in
 *   `data.reason`. So echoing `err.error?.message ?? err.message` — correct for
 *   every other endpoint in this app — would tell a member "you do not have the
 *   necessary access rights" when the truth was `not_joined` or `not_yours`.
 *
 * The resolution order for `message` is therefore:
 *
 *   1. a known `data.reason` → OUR localized copy for that reason;
 *   2. else, the server's own message — but ONLY on the statuses §1.3 says
 *      preserve it (401 · 404-with-a-message · 409 · 422), where it is already
 *      localized, ready to render, and better than anything we could write;
 *   3. else, our localized per-status fallback (400 validation · 401 session ·
 *      403 permission · 404 not found · 409 conflict · 422 invalid · 429 ·
 *      5xx server · offline · timeout).
 *
 * Reason beats server message deliberately: on a 422 the two say the same thing,
 * and on a 403 only the reason is true — so preferring the reason is right in
 * both cases and needs no per-status special-casing.
 */
@Service()
export class OrgErrorMapper {
  private readonly i18n = inject(TranslationFacade);
  private readonly alerts = inject(AlertService);

  /** Normalize any caught value into the display-ready {@link OrgError}. */
  map(err: unknown): OrgError {
    const status = err instanceof ApiError ? err.httpStatus : 0;
    const body = bodyOf(err);
    const data = dataOf(body);
    const reason = toOrgReason(data['reason']);

    return {
      status,
      reason,
      message: this.messageFor(err, status, reason, body),
      fieldErrors: fieldErrorsOf(body),
      // §4.4: `above_max` rejects rather than clamping, and carries the cap so the
      // UI can say what the ceiling actually is.
      max: finiteOrNull(data['max']),
      // §1.2: only a handler-generated 429 carries this.
      retryAfter: finiteOrNull(data['retry_after']),
    };
  }

  /**
   * Map, then surface. Used where the view has no inline slot for the failure —
   * §5's "if the view has no place to render the state → shared alert/toast".
   * Returns the mapped error so the caller can still record it in its own state.
   */
  toast(err: unknown): OrgError {
    const mapped = this.map(err);
    this.alerts.error(mapped.message);
    return mapped;
  }

  /** Success feedback through the SAME shared toast — never `alert()`. */
  success(message: string): void {
    const text = message.trim();
    if (text) this.alerts.success(text);
  }

  /** The localized sentence for a reason, for views that render it inline. */
  reasonMessage(reason: OrgReason): string {
    return this.i18n.translate(ORG_REASON_KEYS[reason]);
  }

  // ─── internals ──────────────────────────────────────────────────────────

  private messageFor(err: unknown, status: number, reason: OrgReason | null, body: unknown): string {
    // 1. A documented refusal reason always wins — it is the only thing that is
    //    true on a 403, and it agrees with the server on 409/422.
    //
    //    `has()` rather than comparing the translation to the key: `translate()`
    //    returns an unresolved key VERBATIM, so a catalogue gap would otherwise
    //    render `reference_portal_org_reason_slot_full` to the member. Asking
    //    first means a gap falls through to the server's own message (step 2) or
    //    our per-status copy (step 3), which are both readable.
    if (reason) {
      const key = ORG_REASON_KEYS[reason];
      if (this.i18n.has(key)) {
        const copy = this.i18n.translate(key).trim();
        if (copy) return copy;
      }
    }

    // 2. The server's own message, where §1.3 says it survived intact.
    if (orgMessageIsTrustworthy(status)) {
      const fromBody = messageOf(body);
      if (fromBody) return fromBody;
      // `ApiError.message` is already display-ready (a raw i18n key is resolved
      // upstream), but it falls back to per-status copy of its own — so only take
      // it when it genuinely came from the API.
      if (err instanceof ApiError && err.fromApi && err.message.trim()) return err.message;
    }

    // 3. Our own localized per-status copy. `timedOut` separates the two status-0
    //    cases the transport collapses together.
    return this.i18n.translate(orgMessageKeyFor(status, isTimeout(err)));
  }
}

// ── body readers, tolerant of all four §1.2 shapes ──────────────────────────

/**
 * The response body, whichever way the failure arrived: `ApiError.raw` holds the
 * `HttpErrorResponse` for a transport error and the envelope itself for an
 * HTTP-200 business error.
 */
function bodyOf(err: unknown): unknown {
  if (err instanceof ApiError) {
    const raw = err.raw;
    return raw instanceof HttpErrorResponse ? raw.error : raw;
  }
  if (err instanceof HttpErrorResponse) return err.error;
  return null;
}

/**
 * `data`, defensively (§1.2: "`body['data'] ?? {}`"). A `clientError()` refusal
 * sends an object; the 403s that carry no reason send `[]`; a raw Laravel
 * validation failure has no `data` key at all. All three end up as `{}` here.
 */
function dataOf(body: unknown): Record<string, unknown> {
  if (!body || typeof body !== 'object') return {};
  const data = (body as { data?: unknown }).data;
  if (!data || typeof data !== 'object' || Array.isArray(data)) return {};
  return data as Record<string, unknown>;
}

/** A non-empty trimmed `message`, from either the envelope or a bare Laravel body. */
function messageOf(body: unknown): string | null {
  if (!body || typeof body !== 'object') return null;
  const message = (body as { message?: unknown }).message;
  return typeof message === 'string' && message.trim() !== '' ? message : null;
}

/**
 * Laravel 422 `{ errors: { field: ["…"] } }`, kept KEYED so a form can bind each
 * line to its own input (§5). Note such a body has neither `status` nor `data`
 * (trap 4) — which is exactly why this reads the top level, not `data`.
 */
function fieldErrorsOf(body: unknown): Readonly<Record<string, readonly string[]>> {
  if (!body || typeof body !== 'object') return {};
  const bag = (body as { errors?: unknown }).errors;
  if (!bag || typeof bag !== 'object' || Array.isArray(bag)) return {};
  const out: Record<string, readonly string[]> = {};
  for (const [field, value] of Object.entries(bag as Record<string, unknown>)) {
    const lines = (Array.isArray(value) ? value : [value])
      .map((line) => (typeof line === 'string' ? line.trim() : ''))
      .filter((line) => line !== '');
    if (lines.length) out[field] = lines;
  }
  return out;
}

function finiteOrNull(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) return Number(value);
  return null;
}

/** rxjs `timeout()` rejects with a `TimeoutError`, which `AppHttp` normalizes to status 0. */
function isTimeout(err: unknown): boolean {
  if (!(err instanceof ApiError)) return (err as { name?: string } | null)?.name === 'TimeoutError';
  return (err.raw as { name?: string } | null)?.name === 'TimeoutError';
}
